import { FocusEvent, InputHTMLAttributes, useRef, useState } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  type UseControllerProps,
} from "react-hook-form";
import { Icon, IconNames, iconNames } from "../Common/Icons/LucideIcons";
import {
  AffixButton,
  AffixContainer,
  AffixText,
  Container,
  Input,
  LeftContainer,
  RightContainer,
} from "./__styles__/TextField";
import {
  FormControl,
  formControlDescribedBy,
  formControlErrorMessageId,
  formControlIds,
  FormControlProps,
} from "./FormControl";

type ContainerVariant =
  | "default"
  | "focused"
  | "error"
  | "disabled"
  | "readonly";

const isIconName = (value: string): value is IconNames =>
  iconNames.includes(value);

type AffixProps = {
  value: string | IconNames;
  disabled?: boolean;
  onPress?: () => void;
  accessibilityLabel?: string;
};

const Affix = ({
  value,
  disabled,
  onPress,
  accessibilityLabel,
}: AffixProps) => {
  const content = isIconName(value) ? (
    <Icon
      iconName={value}
      color={disabled ? "contentDisabled" : "contentSecondary"}
      size={16}
    />
  ) : (
    <AffixText disabled={disabled}>{value}</AffixText>
  );

  if (onPress) {
    return (
      <AffixButton
        type="button"
        onClick={onPress}
        disabled={disabled}
        aria-label={accessibilityLabel}
      >
        {content}
      </AffixButton>
    );
  }

  return <AffixContainer aria-hidden>{content}</AffixContainer>;
};

export type TextFieldProps = {
  hasError?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  rounded?: boolean;
  prefix?: string | IconNames;
  suffix?: string | IconNames;
  onPrefixPress?: () => void;
  onSuffixPress?: () => void;
  prefixAccessibilityLabel?: string;
  suffixAccessibilityLabel?: string;
  testID?: string;
  accessibilityLabel?: string;
  accessibilityHint?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "style">;

export const TextField = ({
  hasError,
  disabled,
  readonly,
  rounded,
  prefix,
  suffix,
  onPrefixPress,
  onSuffixPress,
  prefixAccessibilityLabel,
  suffixAccessibilityLabel,
  testID,
  accessibilityLabel,
  onFocus,
  onBlur,
  placeholder,
  ...inputProps
}: TextFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const containerVariant: ContainerVariant = disabled
    ? "disabled"
    : readonly
      ? "readonly"
      : hasError
        ? "error"
        : isFocused
          ? "focused"
          : "default";

  return (
    <Container
      onClick={handleContainerClick}
      variant={containerVariant}
      rounded={rounded}
    >
      <LeftContainer>
        {prefix && (
          <Affix
            value={prefix}
            disabled={disabled || readonly}
            onPress={onPrefixPress}
            accessibilityLabel={prefixAccessibilityLabel}
          />
        )}
        <Input
          ref={inputRef}
          {...inputProps}
          disabled={disabled}
          readOnly={readonly}
          data-testid={testID}
          placeholder={readonly ? undefined : placeholder}
          aria-label={accessibilityLabel}
          aria-disabled={disabled}
          aria-readonly={readonly}
          aria-invalid={hasError}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </LeftContainer>
      <RightContainer>
        {suffix && (
          <Affix
            value={suffix}
            disabled={disabled || readonly}
            onPress={onSuffixPress}
            accessibilityLabel={suffixAccessibilityLabel}
          />
        )}
      </RightContainer>
    </Container>
  );
};

type HookFormTextFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> &
  Omit<TextFieldProps, "value" | "onChange"> &
  Omit<FormControlProps, "name" | "children" | "errorText">;

export const HookFormTextField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  defaultValue,
  shouldUnregister,
  hasError,
  label,
  required,
  description,
  helperText,
  tooltip,
  ...textFieldProps
}: HookFormTextFieldProps<TFieldValues, TName>) => {
  const finalRules = { ...rules };
  if (required && !finalRules.required) {
    finalRules.required = `${label ?? "This field"} is required`;
  }
  return (
    <Controller
      control={control}
      name={name}
      rules={finalRules}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      render={({ field, fieldState }) => {
        const errorText = fieldState.error?.message;
        return (
          <FormControl
            name={name}
            label={label}
            required={required}
            description={description}
            helperText={helperText}
            errorText={errorText}
            tooltip={tooltip}
          >
            <TextField
              {...textFieldProps}
              id={name}
              aria-labelledby={label ? formControlIds(name).label : undefined}
              aria-describedby={formControlDescribedBy(name, {
                description,
                helperText,
              })}
              aria-errormessage={formControlErrorMessageId(name, errorText)}
              aria-invalid={hasError || !!fieldState.error || undefined}
              value={field.value?.toString() ?? ""}
              onChange={e => field.onChange(e.target.value)}
              onBlur={e => {
                field.onBlur();
                textFieldProps.onBlur?.(e);
              }}
              hasError={hasError || !!fieldState.error}
            />
          </FormControl>
        );
      }}
    />
  );
};
